/**
 * Lista paginada de produtos a partir do `BF_SELLER_DATASETS`.
 *
 * O banco guarda um registro por arquivo, então o catálogo é aberto uma vez por sessão e fica
 * indexado por código no `bfSellerCache`. Daqui para frente a separação continua a mesma:
 *
 *  - o índice guarda um punhado de campos por produto (código, descrição, marca, categoria e os
 *    escopos em que ele tem embalagem) — é o que filtro, busca e ordenação precisam;
 *  - a hidratação monta o produto completo (preço, embalagem recortada, última compra) só para os
 *    itens da página visível.
 *
 * O que sumiu foi a ida ao IndexedDB a cada página: o dado cru já está em memória, e o custo por
 * página passou a ser só a montagem dos produtos que aparecem na tela.
 */

import type { ProductProps } from '@/types';
import { catalogoPorCodigo, precoPorProduto, restricoesPorCliente, ultimaCompraPorCliente } from './bfSellerCache';
import { buildProduct, flattenPreco, mergeProducts } from './bfSellerHydrate';

/** Embalagem reduzida ao que os filtros precisam saber. */
export type ProductIndexPack = {
  /** `QTD_EMBALAGEM` */
  q: number;
  /** `QTD_MULTIPLO_VENDA` */
  m: number;
  /** `SG_EMBALAGEM` */
  s: string;
  loja: number;
  segmento: number;
};

export type ProductIndexEntry = {
  cod: number;
  /** `DESC_PRODUTO` — ordenação/exibição no modo descrição. */
  desc: string;
  /** `DESC_PRODUTO_BUSCA` com fallback para `DESC_PRODUTO`, como as telas já faziam. */
  busca: string;
  marca: number;
  categoria: number;
  unilever: boolean;
  /** `fabricante_grp` já extraído dos ATRIBUTOS — o filtro de fabricante só precisa disso. */
  fabricante: string;
  /**
   * Texto pesquisável já normalizado (descrições + fabricante). Pré-computado aqui porque a busca
   * antes montava essa string a cada tecla, para cada um dos milhares de produtos abertos.
   */
  termos: string;
  /** Códigos pesquisáveis normalizados: EAN das embalagens, DUN, família e o próprio código. */
  codigos: string;
  /** `${COD_LOJA}:${COD_SEGMENTO}` de cada embalagem — filtra por escopo sem montar o produto. */
  scopes: string[];
  /** Embalagens compactadas, para o filtro de disponibilidade sem montar o produto. */
  packs: ProductIndexPack[];
};

export type ProductScope = {
  codLoja?: number | null;
  segmentos?: Array<number | string>;
  codCliente?: number | null;
};

export type ProductSortMode = 'desc' | 'busca';

const scopeTag = (loja: unknown, segmento: unknown) => `${Number(loja) || 0}:${Number(segmento) || 0}`;

/** Normalização de busca: mesma do `cleanStr`, replicada para não puxar o barrel de `@/shared`. */
const normalizeSearch = (value: string, keepEdges = false): string => {
  const cleaned =
    value
      ?.toLowerCase()
      ?.normalize('NFD')
      .replace(/\p{Mn}/gu, '') || value;
  return keepEdges ? (cleaned ?? value) : (cleaned?.trim() ?? value);
};

/** Última ocorrência de `fabricante_grp` nos atributos — mesma varredura de trás para frente do FilterBar. */
const readFabricante = (atributos: any): string => {
  const list = Array.isArray(atributos) ? atributos : [];
  for (let index = list.length - 1; index >= 0; index -= 1) {
    const atributo = list[index];
    if (atributo && 'fabricante_grp' in atributo) return String(atributo.fabricante_grp ?? '');
  }
  return '';
};

/** Uma entrada do índice a partir das linhas cruas do produto (uma por arquivo que o trouxe). */
const buildIndexEntry = (cod: number, linhas: any[]): ProductIndexEntry => {
  const scopes: string[] = [];
  const packs: ProductIndexPack[] = [];
  let eans = '';
  let duns = '';
  let eanGrp = '';
  let familiaGrp = '';

  for (const value of linhas) {
    for (const embalagem of Array.isArray(value?.EMBALAGEM) ? value.EMBALAGEM : []) {
      const loja = Number(embalagem?.COD_LOJA) || 0;
      const segmento = Number(embalagem?.COD_SEGMENTO) || 0;
      const tag = scopeTag(loja, segmento);
      if (!scopes.includes(tag)) scopes.push(tag);

      const multiplo = Number(embalagem?.QTD_MULTIPLO_VENDA) || 1;
      packs.push({
        q: Number(embalagem?.QTD_EMBALAGEM) || 0,
        m: multiplo,
        // Mesma regra da hidratação: múltiplo de venda maior que 1 é PK.
        s: multiplo > 1 ? 'PK' : String(embalagem?.SG_EMBALAGEM ?? ''),
        loja,
        segmento,
      });

      if (embalagem?.EAN_PRODUTO) eans += ` ${embalagem.EAN_PRODUTO} `;
      if (embalagem?.DUN_PRODUTO) duns += ` ${embalagem.DUN_PRODUTO} `;
    }

    for (const atributo of Array.isArray(value?.ATRIBUTOS) ? value.ATRIBUTOS : []) {
      if (atributo && 'ean_grp' in atributo) eanGrp = String(atributo.ean_grp ?? '');
      if (atributo && 'familia_grp' in atributo) familiaGrp = String(atributo.familia_grp ?? '');
    }
  }

  const principal = linhas[0] || {};
  const desc = String(principal?.DESC_PRODUTO ?? '');
  const busca = String(principal?.DESC_PRODUTO_BUSCA || desc);
  const fabricante = readFabricante(principal?.ATRIBUTOS);
  const eanCodes = eans || (eanGrp ? ` ${eanGrp} ` : '');

  return {
    cod,
    desc,
    busca,
    marca: Number(principal?.COD_MARCA) || 0,
    categoria: Number(principal?.COD_CATEGORIA) || 0,
    unilever: principal?.FL_UNILEVER === 'True',
    fabricante,
    termos: normalizeSearch(`${busca} ${desc} ${fabricante}`),
    codigos: normalizeSearch(` ${eanCodes} ${cod} ${familiaGrp} ${duns}`, true),
    scopes,
    packs,
  };
};

/** Índice leve de todos os produtos, um por código. */
export const buildProductIndex = async (): Promise<ProductIndexEntry[]> => {
  const porCodigo = await catalogoPorCodigo();
  const index: ProductIndexEntry[] = [];
  for (const [cod, linhas] of porCodigo) index.push(buildIndexEntry(cod, linhas));
  return index;
};

/** Mesma regra do `getCatalog`: sem embalagem na loja/segmento ativos, o produto não aparece. */
export const filterIndexByScope = (index: ProductIndexEntry[], scope: ProductScope): ProductIndexEntry[] => {
  const loja = Number(scope?.codLoja);
  const hasLoja = Number.isFinite(loja) && loja > 0;
  const segmentos = (scope?.segmentos || []).map((s) => Number(s)).filter((s) => Number.isFinite(s));

  if (!hasLoja && !segmentos.length) return index;

  return index.filter((entry) =>
    entry.scopes.some((tag) => {
      const [tagLoja, tagSegmento] = tag.split(':').map(Number);
      if (hasLoja && tagLoja !== loja) return false;
      if (segmentos.length && !segmentos.includes(tagSegmento)) return false;
      return true;
    })
  );
};

export const sortIndex = (index: ProductIndexEntry[], mode: ProductSortMode = 'busca'): ProductIndexEntry[] =>
  [...index].sort((a, b) => (mode === 'desc' ? a.desc : a.busca).localeCompare(mode === 'desc' ? b.desc : b.busca));

/** Preço dos produtos pedidos, no formato achatado que o `handlePrices` espera. */
export const readPrecoForProdutos = async (codigos: number[]): Promise<Map<number, any[]>> => {
  const byProduto = new Map<number, any[]>();
  if (!codigos.length) return byProduto;

  const porProduto = await precoPorProduto();
  for (const cod of codigos) {
    const valor = porProduto.get(cod);
    if (valor == null) continue;
    byProduto.set(cod, flattenPreco(cod, valor));
  }

  return byProduto;
};

/** Última compra do cliente para os produtos pedidos. */
export const readUltimaCompraForProdutos = async (
  codCliente: number | null | undefined,
  codigos: number[]
): Promise<Map<number, any>> => {
  const out = new Map<number, any>();
  const cliente = Number(codCliente);
  if (!Number.isFinite(cliente) || !codigos.length) return out;

  const porCliente = await ultimaCompraPorCliente();
  const doCliente = porCliente.get(cliente);
  if (!doCliente) return out;

  for (const cod of codigos) {
    const item = doCliente[cod];
    if (item) out.set(cod, item);
  }
  return out;
};

/** Produtos que o cliente não pode comprar. */
export const readRestricoesDoCliente = async (codCliente: number | null | undefined): Promise<Set<number>> => {
  const cliente = Number(codCliente);
  if (!Number.isFinite(cliente)) return new Set<number>();

  const porCliente = await restricoesPorCliente();
  return new Set((porCliente.get(cliente) || []).map(Number));
};

const filterEmbalagemByScope = (embalagem: any[], scope: ProductScope): any[] => {
  const loja = Number(scope?.codLoja);
  const hasLoja = Number.isFinite(loja) && loja > 0;
  const segmentos = (scope?.segmentos || []).map((s) => Number(s)).filter((s) => Number.isFinite(s));

  return embalagem.filter((emb) => {
    const lojaOk = !hasLoja || Number(emb?.COD_LOJA) === loja;
    const segmentoOk = !segmentos.length || segmentos.includes(Number(emb?.COD_SEGMENTO));
    return lojaOk && segmentoOk;
  });
};

/** Junta as linhas cruas de um produto num `ProductProps`, com preço e última compra dentro. */
const montarProduto = (linhas: any[], cod: number, preco: any[], ultimaCompra: any): ProductProps | undefined => {
  let product: ProductProps | undefined;
  for (const item of linhas) {
    const built = buildProduct(item, preco, ultimaCompra);
    product = product ? mergeProducts(product, built) : built;
  }
  if (product) product.COD_PRODUTO = cod as any;
  return product;
};

/**
 * Monta os produtos de uma página: catálogo + preço + última compra, com a embalagem recortada
 * para a loja/segmento ativos — o mesmo produto que o `getCatalog` montava.
 */
export const hydrateProducts = async (entries: ProductIndexEntry[], scope: ProductScope): Promise<ProductProps[]> => {
  if (!entries.length) return [];

  const codigos = entries.map((entry) => entry.cod);
  const [porCodigo, precoByProduto, ultimaCompraByProduto, restritos] = await Promise.all([
    catalogoPorCodigo(),
    readPrecoForProdutos(codigos),
    readUltimaCompraForProdutos(scope?.codCliente, codigos),
    readRestricoesDoCliente(scope?.codCliente),
  ]);

  const out: ProductProps[] = [];
  for (const entry of entries) {
    if (restritos.has(entry.cod)) continue;

    const linhas = porCodigo.get(entry.cod);
    if (!linhas?.length) continue;

    const product = montarProduto(linhas, entry.cod, precoByProduto.get(entry.cod) ?? [], ultimaCompraByProduto.get(entry.cod));
    if (!product) continue;

    // Descarta quem ficou sem embalagem no escopo ativo.
    const EMBALAGEM = filterEmbalagemByScope(product.EMBALAGEM || [], scope);
    if (!EMBALAGEM.length) continue;
    out.push({ ...product, EMBALAGEM });
  }
  return out;
};

/** Uma página do índice já hidratada. `page` é 1-based. */
export const readProductPage = async ({
  index,
  page,
  pageSize,
  scope,
}: {
  index: ProductIndexEntry[];
  page: number;
  pageSize: number;
  scope: ProductScope;
}): Promise<{ items: ProductProps[]; total: number; hasMore: boolean }> => {
  const total = index.length;
  const end = Math.max(0, page) * pageSize;
  const slice = index.slice(0, end);
  const items = await hydrateProducts(slice, scope);
  return { items, total, hasMore: end < total };
};

/** Um produto pelo código, sem montar índice nenhum: o catálogo já está indexado por código. */
export const readProductByCode = async (codProduto: number, scope: ProductScope = {}): Promise<ProductProps | null> => {
  const cod = Number(codProduto);
  if (!Number.isFinite(cod)) return null;

  const porCodigo = await catalogoPorCodigo();
  const linhas = porCodigo.get(cod);
  if (!linhas?.length) return null;

  const [precoByProduto, ultimaCompraByProduto] = await Promise.all([
    readPrecoForProdutos([cod]),
    readUltimaCompraForProdutos(scope?.codCliente, [cod]),
  ]);

  const product = montarProduto(linhas, cod, precoByProduto.get(cod) ?? [], ultimaCompraByProduto.get(cod));
  if (!product) return null;

  const hasScopeFilter = !!scope?.codLoja || !!scope?.segmentos?.length;
  if (!hasScopeFilter) return product;

  const EMBALAGEM = filterEmbalagemByScope(product.EMBALAGEM || [], scope);
  return { ...product, EMBALAGEM };
};

/**
 * Quais embalagens têm preço, por produto, dentro de uma loja/segmento.
 *
 * É o que o filtro de disponibilidade precisa saber sem montar produto nenhum: o
 * `isProductAvailable` confere, para cada embalagem, se existe preço daquele pack.
 */
export const readPackQtdsComPreco = async (scope: ProductScope): Promise<Map<number, Set<number>>> => {
  const porProduto = new Map<number, Set<number>>();
  const loja = Number(scope?.codLoja);
  const hasLoja = Number.isFinite(loja) && loja > 0;
  const segmentos = (scope?.segmentos || []).map((s) => Number(s)).filter((s) => Number.isFinite(s));

  const preco = await precoPorProduto();
  for (const [produto, valor] of preco) {
    if (valor == null || typeof valor !== 'object') continue;

    for (const [rowLoja, porSegmento] of Object.entries(valor as Record<string, any>)) {
      if (hasLoja && Number(rowLoja) !== loja) continue;
      if (porSegmento == null || typeof porSegmento !== 'object') continue;

      for (const [rowSegmento, embalagens] of Object.entries(porSegmento as Record<string, any>)) {
        if (segmentos.length && !segmentos.includes(Number(rowSegmento))) continue;
        if (embalagens == null || typeof embalagens !== 'object') continue;

        let qtds = porProduto.get(produto);
        if (!qtds) {
          qtds = new Set<number>();
          porProduto.set(produto, qtds);
        }
        for (const qtd of Object.keys(embalagens as Record<string, unknown>)) qtds.add(Number(qtd));
      }
    }
  }

  return porProduto;
};

/** Códigos que o cliente já comprou — a base da visão "Últimas compras". */
export const readUltimaCompraCodigos = async (codCliente: number | null | undefined): Promise<number[]> => {
  const cliente = Number(codCliente);
  if (!Number.isFinite(cliente)) return [];

  const porCliente = await ultimaCompraPorCliente();
  const doCliente = porCliente.get(cliente);
  if (!doCliente) return [];

  const codigos: number[] = [];
  for (const produto of Object.keys(doCliente)) {
    const cod = Number(produto);
    if (Number.isFinite(cod)) codigos.push(cod);
  }
  return codigos;
};

/**
 * Abre um conjunto conhecido de produtos direto pelo código, sem passar pelo índice.
 *
 * É o caminho do carrinho, do checkout e das telas de pedido: eles sabem exatamente quais
 * produtos precisam (os itens), e nenhum deles quer o catálogo montado inteiro.
 */
export const readProductsByCodes = async (codigos: number[], scope: ProductScope = {}): Promise<ProductProps[]> => {
  const codes = Array.from(new Set(codigos.map(Number).filter((c) => Number.isFinite(c))));
  if (!codes.length) return [];

  const [porCodigo, precoByProduto, ultimaCompraByProduto] = await Promise.all([
    catalogoPorCodigo(),
    readPrecoForProdutos(codes),
    readUltimaCompraForProdutos(scope?.codCliente, codes),
  ]);

  const hasScopeFilter = !!scope?.codLoja || !!scope?.segmentos?.length;
  const out: ProductProps[] = [];

  for (const cod of codes) {
    const linhas = porCodigo.get(cod);
    if (!linhas?.length) continue;

    const product = montarProduto(linhas, cod, precoByProduto.get(cod) ?? [], ultimaCompraByProduto.get(cod));
    if (!product) continue;

    out.push(hasScopeFilter ? { ...product, EMBALAGEM: filterEmbalagemByScope(product.EMBALAGEM || [], scope) } : product);
  }

  return out;
};
